import type { QueryResult } from '@apollo/client';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import Pager from '@aurora/shared-client/components/common/Pager/Pager';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import PaginationHelper from '@aurora/shared-client/helpers/ui/PaginationHelper/PaginationHelper';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { Connection } from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './PaneledItemList.module.pcss';

/**
 * Props for the PaneledItemList Pager
 */
interface Props<TData, TVariables> {
  /**
   * The query result from a GraphQL call.
   */
  queryResult: QueryResult<TData, TVariables>;
  /**
   * The style of pager to use.
   */
  pagerVariant?: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams>;
  /**
   * The number of items to show per page.
   */
  pageSize?: number;
  /**
   * The path to the items in the query results. This is used to get the items from
   * the query result for display and used for updating the items during pagination.
   * If left unspecified then the item path is assumed to simply be the key of the first
   * property inside the `data` object.
   */
  itemPath?: string;
  /**
   * Callback after pagination has occurred, can be used to augment API results
   * to "link" them to existing objects in the Apollo cache.
   */
  onUpdate?: ((newConnection: Connection) => TData) | undefined;
  /**
   * Callback function to fetch current page state
   */
  onPageChange?: (newConnection: Connection, variables: TVariables) => void;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
}

/**
 * The pager for a list of items that are rendered inside a Panel.
 * @author rishabh.jivan
 */
const PaneledItemListPager = <TData, TVariables>({
  queryResult,
  pagerVariant = { type: PagerVariant.LOAD_MORE },
  pageSize = 5,
  itemPath,
  onUpdate,
  onPageChange,
  className
}: Props<TData, TVariables>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const { Link } = useEndUserRoutes();
  const paginationHelper = PaginationHelper.fromQueryResult<TData, TVariables>(
    queryResult,
    pageSize,
    itemPath,
    onUpdate,
    onPageChange
  );

  const hasNextPage: boolean = paginationHelper?.pageInfo?.hasNextPage;
  const hasPreviousPage: boolean = paginationHelper?.pageInfo?.hasPreviousPage;
  const isPageable: boolean =
    pagerVariant.type === PagerVariant.PREVIOUS_NEXT ||
    pagerVariant.type === PagerVariant.PREVIOUS_NEXT_LINKABLE
      ? hasNextPage || hasPreviousPage
      : hasNextPage;

  let finalPagerVariant = pagerVariant;

  if (pagerVariant.type === PagerVariant.SEE_ALL_MODAL) {
    finalPagerVariant = {
      type: pagerVariant.type,
      props: {
        ...pagerVariant.props,
        Link
      }
    };
  }

  if (isPageable) {
    return (
      <Pager
        loadPage={paginationHelper.loadPage}
        pageInfo={paginationHelper.pageInfo}
        variant={finalPagerVariant}
        className={cx(className)}
      />
    );
  }

  return null;
};

export default PaneledItemListPager;
